import p5 from "p5";

export class DistortApp {
  private p?: any;
  private container: HTMLElement | null = null;

  // 風車グリッド設定
  private cols = 35;
  private rows = 20;
  private padding = 2;
  private hoverRadius = 0;
  private windmills: Windmill[] = [];

  initialize(): void {
    this.container = document.getElementById("p5-container");
    if (!this.container) throw new Error("#p5-container が見つかりません");

    const sketch = (p: any) => {
      p.setup = () => {
        const canvas = p.createCanvas(p.windowWidth, p.windowHeight);
        canvas.parent(this.container!);
        p.pixelDensity(1);
        p.angleMode(p.RADIANS);
        p.colorMode(p.HSB, 360, 100, 100, 100);

        createWindmills();
      };

      p.draw = () => {
        p.background(255);
        for (const w of this.windmills) {
          w.update(p, this.hoverRadius);
          w.display(p);
        }
      };

      p.windowResized = () => {
        p.resizeCanvas(p.windowWidth, p.windowHeight);
        createWindmills();
      };

      const createWindmills = () => {
        this.windmills = [];
        const cellWidth = p.width / this.cols;
        const cellHeight = p.height / this.rows;
        const windmillSize = Math.min(cellWidth, cellHeight) - this.padding;

        this.hoverRadius = Math.min(cellWidth, cellHeight) * 5.0;

        for (let i = 0; i < this.cols; i++) {
          for (let j = 0; j < this.rows; j++) {
            const x = cellWidth * i + cellWidth / 2;
            const y = cellHeight * j + cellHeight / 2;
            const center = p.createVector(p.width / 2, p.height / 2);
            const pos = p.createVector(x, y);
            const delay = p.dist(center.x, center.y, pos.x, pos.y) * 0.1;
            this.windmills.push(new Windmill(p, x, y, windmillSize, delay));
          }
        }
      };
    };

    this.p = new p5(sketch);
  }

  destroy(): void {
    if (this.p) {
      this.p.remove();
      this.p = undefined;
    }
  }
}

class Windmill {
  private p: any;
  private pos: any;
  private targetSize: number;
  private currentSize: number;
  private isAppeared: boolean;
  private appearDelay: number;
  private appearFrame: number;

  private canStartRotating: boolean;
  private rotateDelay: number;

  private normalSpeed: number;
  private fastSpeed: number;
  private rotationSpeed: number;
  private rotationAngle: number;

  private color1: any;
  private color2: any;
  private bladeCount: number;

  constructor(p: any, x: number, y: number, size: number, delay: number) {
    this.p = p;
    this.pos = p.createVector(x, y);
    this.targetSize = size;
    this.currentSize = 0;
    this.isAppeared = false;
    this.appearDelay = delay;
    this.appearFrame = p.frameCount;

    this.canStartRotating = false;
    this.rotateDelay = delay + 60;

    this.normalSpeed = 0.03;
    this.fastSpeed = this.normalSpeed * 20;
    this.rotationSpeed = this.normalSpeed;
    this.rotationAngle = p.random(this.p.TWO_PI);

    this.color1 = p.color(p.random(360), 85, 100);
    this.color2 = p.color(p.random(360), 85, 100);
    this.bladeCount = 4;
  }

  update(p: any, hoverRadius: number) {
    if (p.frameCount > this.appearFrame + this.appearDelay) {
      this.currentSize = p.lerp(this.currentSize, this.targetSize, 0.1);
    }
    if (!this.isAppeared && Math.abs(this.currentSize - this.targetSize) < 0.1) {
      this.isAppeared = true;
    }

    if (!this.canStartRotating && p.frameCount > this.appearFrame + this.rotateDelay) {
      this.canStartRotating = true;
    }

    if (this.canStartRotating) {
      this.rotationAngle += this.rotationSpeed;

      let targetSpeed: number;

      if (p.mouseX >= 0 && p.mouseX <= p.width && p.mouseY >= 0 && p.mouseY <= p.height) {
        const d = p.dist(this.pos.x, this.pos.y, p.mouseX, p.mouseY);
        if (d < hoverRadius) {
          targetSpeed = p.map(d, 0, hoverRadius, this.fastSpeed, this.normalSpeed);
        } else {
          targetSpeed = this.normalSpeed;
        }
      } else {
        targetSpeed = this.normalSpeed;
      }

      targetSpeed = Math.max(this.normalSpeed, targetSpeed);
      this.rotationSpeed = p.lerp(this.rotationSpeed, targetSpeed, 0.05);
    }
  }

  display(p: any) {
    if (this.currentSize < 0.1) return;

    p.push();
    p.translate(this.pos.x, this.pos.y);
    const currentBladeLength = this.currentSize / 2;

    // 奥の風車
    p.push();
    p.rotate(this.rotationAngle);
    p.fill(this.color1);
    p.noStroke();
    for (let i = 0; i < this.bladeCount; i++) {
      p.rotate(p.TWO_PI / this.bladeCount);
      this.drawBlade(p, currentBladeLength);
    }
    p.pop();

    // 手前の風車
    p.push();
    const offsetAngle = p.TWO_PI / this.bladeCount / 2.0;
    p.rotate(this.rotationAngle + offsetAngle);
    p.fill(this.color2);
    p.noStroke();
    for (let i = 0; i < this.bladeCount; i++) {
      p.rotate(p.TWO_PI / this.bladeCount);
      this.drawBlade(p, currentBladeLength);
    }
    p.pop();

    // 留め具
    p.fill(40, 80, 60);
    p.noStroke();
    p.ellipse(0, 0, this.currentSize * 0.1, this.currentSize * 0.1);
    p.pop();
  }

  private drawBlade(p: any, bladeLength: number) {
    const bladeCurve = bladeLength * 0.5;
    const midX = bladeLength * 0.35;
    // 2つの三角形で羽根形状を構成（パス変換を使わない安全描画）
    p.triangle(0, 0, midX, -bladeCurve, bladeLength, 0);
    p.triangle(0, 0, bladeLength, 0, midX, bladeCurve * 0.25);
  }
}
